//#region TYPES
/**
 * @typedef {Object} GA4Options
 * @description Available options to initialize GA4
 * @property {boolean} [enable=false] Enables or not the GA logging system
 */

type GA4Options = {
	/**
	 * Enables or not the GA logging system
	 * @default false
	 */
	enable?: boolean;
}

type GA4Commands = 'config' | 'get' | 'set' | 'event' | 'consent';

type GtagFunction = ((command: GA4Commands, commandParameter: string | object, additionalInfo?: string | object, callback?: (fieldValue?: any) => any) => void)
					| undefined

/**
 * GA4 function to send data to Google Analytics
 * @see https://developers.google.com/gtagjs/reference/api
 */
declare var gtag: GtagFunction;

//#endregion TYPES

var _gaCode: string = process.env.NEXT_PUBLIC_GA_ID || '';

/**
 * GA4 function to send data to Google Analytics.<br>
 * This method has builtin validation in case GA4 could not be initialize. Use this instead of `gtag`.
 * @param {'config'|'get'|'set'|'event'|'consent'} command
 * @param {string|object} commandParameter The parameters to be passed to GA script. Vary according to the command. Check the {@link https://developers.google.com/gtagjs/reference/api documentation}
 * @param {string|object} additionalInfo Vary according to the command. Check the {@link https://developers.google.com/gtagjs/reference/api documentation}
 * @param {Function} callback A function that will be invoked with the requested field, or undefined if it is unset.
 * @see https://developers.google.com/gtagjs/reference/api
 */
export const safeGtag: GtagFunction = function(){
	// @ts-ignore
	if(window.gtag){
		// @ts-ignore
		gtag(...arguments);
	}
}

/**
 * Initialize Google Analytics 4.<br>
 * For NextJS app, set `process.env.NEXT_PUBLIC_GA_ID` and don't call this function. {@link https://medium.com/@mikegajdos81/how-to-add-googleanalytics-4-to-nextjs-app-in-4-simple-steps-3c6f9de2f866 GA4 with NextJS}
 * @param {string} gaCode The GA4 code. `G-XXXXXXXXXX`
 * @param {GA4Options} [options]
 * @param {boolean} options.enable If we want to disable GA4 or not
 * @returns {Promise}
 */
export const initializeGA = async (gaCode: string, options: GA4Options = {}) => {
	// Do some init stuff
	_gaCode = gaCode;
	return new Promise<void>((resolve, reject) => {
		// Global site tag (gtag.js) - Google Analytics 4
		const analyticsGtag = document.createElement('script');
		analyticsGtag.src = `https://www.googletagmanager.com/gtag/js?id=${_gaCode}`;
		analyticsGtag.onload = () => {
			const initScript = document.createElement('script');

			const scriptHTML = `
				${options.enable ? '' : `window['ga-disable-${_gaCode}'] = true;`}
				window.dataLayer = window.dataLayer || [];
				function gtag(){dataLayer.push(arguments);};
				window.gtag = gtag;
				gtag('js', new Date());
				gtag('set', 'send_page_view', false);
				gtag('config', '${_gaCode}');
			`;

			initScript.type = 'text/javascript';
			initScript.innerHTML = scriptHTML;
			document.head.appendChild(initScript);
			document.head.removeChild(analyticsGtag);
			resolve();
		};

		analyticsGtag.onerror = (event) => {
			if(typeof event === "string"){
				reject(new Error(`Failed to load ${event}`));
			}else{
				// @ts-ignore
				reject(new Error(`Failed to load ${event.target?.src}`));
			}
		};

		analyticsGtag.onabort = (event) => {
			// @ts-ignore
			reject(new Error(`Failed to load ${event.target?.src}`));
		}

		document.head.appendChild(analyticsGtag);
	})
}

/**
 * Get the GACode from witch GA4 was initialize with.
 * @returns {string} The GACode initialized with
 */
export const getGaCode = (): string => _gaCode;

/**
 * Get various values including values set with the {@link GoogleAnalytics.set set} command
 * @param {string} fieldName The name of the field to get.
 * @returns {Promise<any>}
 * @see https://developers.google.com/gtagjs/reference/api#get
 */
export const getGAField = (fieldName: string): Promise<any> => {
	return new Promise(resolve => {
		safeGtag?.('get', _gaCode, fieldName, resolve);
	});
}

/**
 * Allows you to set values that persist across all the subsequent events
 * @param {object.<string, *>} values
 * @see https://developers.google.com/gtagjs/reference/api#set
 */
export const setGA = (values: Object) => {
	safeGtag("set", values);
}

/**
 * It enables the analysis of groups of sessions across devices
 * @param {string} userId A unique, persistent, and non-personally identifiable ID string that represents a user
 * @see https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id#set_user_id
 */
export const setGAUser = (userId: string) => {
	safeGtag('config', _gaCode, {
		'user_id': userId
	});
}

/**
 * User properties are attributes that can be used to describe the user base.
 * @param {object.<string, *>} properties User properties. Usefull for {@link https://support.google.com/analytics/answer/10075209?visit_id=637612755092114421-2855772301&rd=1 user reports}. Maximum of 25 additional data.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/user-properties
 */
export const setGAUserProperties = (properties: Object) => {
	safeGtag('config', _gaCode, {
		'user_properties': properties
	});
}

/**
 * This event signifies a user has viewed a page.
 * @param {string} [pathName=window.location.pathname] The path to the page. If overridden, this value must start with a `/` character. Defaults to `window.location.pathname`
 * @param {string} [location=window.location.href] The URL of the page. Defaults to `window.location.href`
 * @param {string} [title] The title of the page. Defaults to `pathName` then to `document.title`
 * @see https://developers.google.com/gtagjs/reference/event#page_view
 */
export const GApageView = (pathName: string = window.location.pathname, location: string = window.location.href, title: string) => {
	safeGtag("event", "page_view", {
		event_category: 'user_click',
		page_location: location,
		page_path: pathName,
		page_title: title ?? pathName ?? document.title,
	});
}

/**
 * Allows to add additional configuration information
 * @param {Object} config
 * @see https://developers.google.com/gtagjs/reference/api#config
 */
export const setGAConfig = (config: Object) => {
	safeGtag('config', _gaCode, config);
}

/**
 * Will send event data
 * @param {string} eventName The event name. Here is a list of {@link https://developers.google.com/gtagjs/reference/ga4-events recommended events}
 * @param {*} eventParams Data to be send with this event
 * @see https://developers.google.com/analytics/devguides/collection/gtagjs/events
 */
export const sendGAEvent = (eventName: string, eventParams: Object) => {
	safeGtag('event', eventName, eventParams);
}

/**
 * Will send event data to tell that a modal was oppened or closed
 * @param {string} name The modal name. Helps to know which modal was shown in GA
 * @param {'OPEN'|'CLOSE'} [state='OPEN'] If the modal is being oppened or closed.
 * @param {object.<string, *>} [extraData] Data to be send with this event
 */
export const GAmodalView = (name: string, state: 'OPEN'|'CLOSE' = 'OPEN', extraData: Object) => {
	safeGtag('event', 'modal_view', {
		event_category: 'user_click',
		event_label: 'Modal view',
		state: state,
		name: name,
		...extraData,
	});
}

/**
 * Send an exception event to Google Analytics
 * @param {string} description A description of the error.
 * @param {boolean} [fatal=false] If the error was fatal.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/exceptions
 */
export const captureGAError = (description: string, fatal: boolean = false) => {
	safeGtag('event', 'exception', {
		description: description,
		fatal: fatal,
	});
}
